/**
 * SetPasswordModal — the Owner sets a real member's password (v53, 29 Oct
 * 2026: Stuart, "I should be able to reset and set any password in the SAP").
 * Backend: POST /api/platform/users/:id/set-password { password, code } — Owner
 * only, needs a code from the Owner's authenticator app (set up on the
 * Dashboard), never staff or children. The person is emailed that Marakin
 * support set a new password, and signed out everywhere else. The password is
 * never stored or shown anywhere after Save.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { usePlatformAuth } from '../../context/PlatformAuthContext';
import { X, AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react';

const MIN_LENGTH = 8; // backend/src/lib/password.js MIN_PASSWORD_LENGTH

export default function SetPasswordModal({ member, onClose }) {
  const { platformAxios } = usePlatformAuth();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState(null); // { text, needsSetup }
  const [done, setDone] = useState(null);   // { emailed }
  const name = [member.first_name, member.last_name].filter(Boolean).join(' ') || member.email;

  const save = useMutation({
    mutationFn: () => platformAxios.post(`/users/${member.user_id}/set-password`, { password, code }).then(r => r.data),
    onSuccess: (data) => { setPassword(''); setCode(''); setDone({ emailed: data?.emailed !== false }); },
    onError: (err) => {
      const d = err.response?.data || {};
      setCode('');
      setError({ text: d.error || 'Could not set the password', needsSetup: d.code === 'authenticator_needed' });
    },
  });

  const cleanCode = code.replace(/\s/g, '');
  const ready = password.length >= MIN_LENGTH && /^\d{6}$/.test(cleanCode);
  const submit = (e) => {
    e.preventDefault();
    if (!ready || save.isPending) return;
    setError(null);
    save.mutate();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Set password</h2>
          <button className="btn-close" onClick={onClose} aria-label="Close"><X size={24} /></button>
        </div>

        {done ? (
          <div className="modal-form">
            <div className="test-pw-done">
              <CheckCircle2 size={20} />
              <p>
                Password set. Their other sessions were signed out
                {done.emailed ? ', and they’ve been emailed that Marakin support changed it.' : '.'}
              </p>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-primary" onClick={onClose}>Done</button>
            </div>
          </div>
        ) : (
          <form className="modal-form" onSubmit={submit}>
            {/* QA N2: no username hint and autoComplete off — a real member's
                password must never end up saved in the Owner's password manager. */}
            <p className="test-pw-intro">
              For <strong>{name}</strong>{member.email ? ` (${member.email})` : ''}. They’ll be signed out on every
              device and emailed that Marakin support set a new password. Only do this when they’ve asked you to.
            </p>
            {error && (
              <div className="error-box" style={{ marginBottom: 12 }}>
                <AlertCircle size={16} />
                <p>
                  {error.text}
                  {error.needsSetup && <> <Link to="/marakin-admin" onClick={onClose}>Go to the Dashboard</Link></>}
                </p>
              </div>
            )}
            <div className="form-group">
              <label htmlFor="set-pw-input">New password</label>
              <div className="test-pw-field">
                <input
                  id="set-pw-input"
                  type={show ? 'text' : 'password'}
                  autoComplete="off"
                  data-1p-ignore
                  data-lpignore="true"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button type="button" className="test-pw-toggle" onClick={() => setShow(s => !s)}
                  aria-label={show ? 'Hide password' : 'Show password'}>
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <p className="test-pw-hint">At least {MIN_LENGTH} characters.</p>
            </div>
            <div className="form-group">
              <label htmlFor="set-pw-code">Code from your authenticator app</label>
              <input
                id="set-pw-code"
                className="authenticator-code-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={7}
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, ''))}
              />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={!ready || save.isPending}>
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
